import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { CheckCircle, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface ThumbnailSelectOption {
  value: string;
  label: string;
  thumbnail?: string; // Optional thumbnail URL
}

interface ThumbnailSelectProps {
  options: ThumbnailSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  compact?: boolean;
}

// Simple hash function to generate a color from a string for placeholder thumbnails
const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

export const ThumbnailSelect: React.FC<ThumbnailSelectProps> = ({ options, value, onChange, placeholder, disabled = false, compact = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (selectedValue: string) => {
    // If clicking the same option, deselect it (set to empty/none)
    if (selectedValue === value) {
      onChange('');
    } else {
      onChange(selectedValue);
    }
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          disabled={disabled}
          className={`w-full justify-between text-left font-medium ${selectedOption ? 'h-auto min-h-12 py-3' : 'h-12'} px-4 bg-background/50 border border-border/50 hover:bg-background/70 hover:border-border/70 rounded-xl transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/50 backdrop-blur-sm ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <div className="flex flex-col items-start w-full">
            <span className="text-xs text-muted-foreground font-medium">
              {placeholder}
            </span>
            {selectedOption && (
              <span className="text-sm text-foreground font-semibold leading-tight mt-1 whitespace-pre-line">
                {selectedOption.label}
              </span>
            )}
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className={`${compact ? 'max-w-2xl' : 'max-w-4xl'} max-h-[85vh] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-700 shadow-2xl p-0`}>
        <DialogHeader className="pb-3 pr-8 px-4 pt-4 sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700">
          <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white flex items-center space-x-2">
            <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
            </div>
            <span>{placeholder}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(85vh-80px)] px-4 pb-4">
          <div className={`grid gap-3 py-2 ${
            options.length === 3 
              ? 'grid-cols-3' 
              : options.length === 6
              ? 'grid-cols-3'
              : 'grid-cols-3 md:grid-cols-4'
          }`}>
            {options.map((option) => {
              const isSelected = value === option.value;
              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={clsx(
                    'cursor-pointer group space-y-2 transform transition-all duration-200 ease-in-out rounded-lg overflow-hidden border-2',
                    {
                      'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-lg shadow-indigo-500/20 scale-105': isSelected,
                      'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:scale-102 hover:shadow-md': !isSelected,
                    }
                  )}
                >
                  <div className="aspect-square w-full overflow-hidden relative bg-slate-50 dark:bg-slate-800">
                    {option.thumbnail ? (
                      <img src={option.thumbnail} alt={option.label} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full" style={{ backgroundColor: stringToColor(option.label) }}></div>
                    )}
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-white dark:bg-slate-800 rounded-full p-0.5 shadow-md border border-indigo-200 dark:border-indigo-700">
                        <CheckCircle className="h-4 w-4 text-indigo-500" />
                      </div>
                    )}
                  </div>
                  <p className={clsx(
                    'text-xs text-center font-medium transition-colors px-1 pb-1',
                    { 
                      'text-indigo-600 dark:text-indigo-400': isSelected,
                      'text-slate-700 dark:text-slate-300': !isSelected
                    }
                  )}>
                    {option.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};